import React, { useEffect, useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const formatBytes = (bytes) => {
  if (bytes == null || bytes === 0) return '0 B';
  if (bytes >= 1073741824) return (bytes / 1073741824).toFixed(1) + ' GB';
  if (bytes >= 1048576) return (bytes / 1048576).toFixed(1) + ' MB';
  if (bytes >= 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return bytes + ' B';
};
const bytesToMb = (bytes) => (bytes == null ? '' : Math.round(bytes / (1024 * 1024)));

const inputCls = 'w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100 bg-slate-50 focus:bg-white transition-all';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const initialFormState = { name: '', email: '', phone: '', disk_quota_mb: '' };
  const [formData, setFormData] = useState(initialFormState);
  const [modalMode, setModalMode] = useState('add');
  const [editingUser, setEditingUser] = useState(null);
  const [deleteConfirmUser, setDeleteConfirmUser] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
      };
      if (modalMode !== 'edit') {
        const quotaMb = formData.disk_quota_mb === '' || formData.disk_quota_mb == null
          ? null : parseInt(String(formData.disk_quota_mb).replace(/\D/g, ''), 10);
        payload.disk_quota_mb = isNaN(quotaMb) ? null : quotaMb;
      }
      if (!payload.name || !payload.email || !payload.phone) {
        alert('Please fill name, email, and phone.');
        return;
      }
      if (modalMode === 'edit' && editingUser) {
        await axios.put(`${API_URL}/users/${editingUser.id}`, payload);
        setUsers(users.map(u => u.id === editingUser.id ? { ...u, name: payload.name, email: payload.email, phone: payload.phone } : u));
        setShowModal(false); setFormData(initialFormState); setEditingUser(null);
        alert('User updated successfully.');
      } else {
        const response = await axios.post(`${API_URL}/users`, payload);
        setUsers([response.data, ...users]);
        setShowModal(false); setFormData(initialFormState);
        alert(`User onboarded: ${response.data.name}.`);
      }
    } catch (error) {
      const msg = error.code === 'ERR_NETWORK' || !error.response
        ? 'Cannot reach server.'
        : (error.response?.data?.error || error.message || 'Unknown error');
      alert(`Error: ${msg}`);
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteConfirmUser) return;
    setDeleting(true);
    try {
      const res = await axios.delete(`${API_URL}/users/${deleteConfirmUser.id}`);
      setUsers(users.filter((u) => u.id !== deleteConfirmUser.id));
      setDeleteConfirmUser(null);
      const count = res.data.batches_deleted ?? 0;
      alert(count > 0 ? `User deleted. ${count} batch(es) removed.` : 'User deleted.');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete user.');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <div className="w-12 h-12 rounded-2xl bg-violet-50 flex items-center justify-center">
          <div className="w-6 h-6 border-2 border-violet-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Users</h1>
          <p className="text-sm text-slate-500 mt-0.5">{users.length} user{users.length !== 1 ? 's' : ''}</p>
        </div>
        <button
          onClick={() => { setModalMode('add'); setEditingUser(null); setFormData(initialFormState); setShowModal(true); }}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white shadow-sm hover:shadow-md active:scale-95 transition-all"
          style={{ background: 'linear-gradient(135deg, #7c3aed, #4f46e5)' }}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
          </svg>
          Add User
        </button>
      </div>

      {/* User cards */}
      {users.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-12 text-center">
          <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
            <svg className="w-7 h-7 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          </div>
          <p className="text-slate-500 text-sm">No users found</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {users.map((user) => {
            const quota = user.disk_quota_bytes != null ? Number(user.disk_quota_bytes) : null;
            const consumed = Number(user.disk_consumed_bytes || 0);
            const pct = quota != null && quota > 0 ? Math.round((consumed / quota) * 100) : null;
            return (
              <div key={user.id} className="bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow overflow-hidden">
                <div className="p-4">
                  {/* Actions */}
                  <div className="flex justify-end gap-1 mb-3">
                    <button
                      onClick={() => { setModalMode('edit'); setEditingUser(user); setFormData({ name: user.name, email: user.email, phone: user.phone, disk_quota_mb: user.disk_quota_bytes != null ? String(bytesToMb(user.disk_quota_bytes)) : '' }); setShowModal(true); }}
                      className="p-1.5 rounded-lg text-violet-600 hover:bg-violet-50 transition-colors" title="Edit"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                    </button>
                    <button
                      onClick={() => setDeleteConfirmUser(user)}
                      className="p-1.5 rounded-lg text-rose-500 hover:bg-rose-50 transition-colors" title="Delete"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                    </button>
                  </div>

                  {/* Avatar + name */}
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 text-white text-sm font-bold" style={{ background: 'linear-gradient(135deg, #7c3aed, #4f46e5)' }}>
                      {(user.name || '?')[0].toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-slate-900 truncate">{user.name}</p>
                      <p className="text-xs text-slate-400 truncate">{user.phone}</p>
                    </div>
                  </div>

                  <div className="space-y-1">
                    <p className="text-xs text-slate-500 truncate">{user.email}</p>
                    <p className="text-xs text-slate-400">
                      {user.created_at ? new Date(user.created_at).toLocaleDateString(undefined, { dateStyle: 'medium' }) : '—'}
                      {user.created_by_username ? ` · by ${user.created_by_username}` : ''}
                    </p>
                  </div>

                  {/* Plan badge */}
                  {user.plan_display && (
                    <div className="mt-2">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-violet-50 text-violet-700">
                        <span className="w-1.5 h-1.5 rounded-full bg-violet-500" />
                        {user.plan_display}
                      </span>
                    </div>
                  )}

                  {/* Stats */}
                  <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-3 text-xs text-slate-400">
                    <span>{user.batch_count || 0} batches</span>
                    <span>{user.photo_count || 0} photos</span>
                  </div>

                  {/* Storage bar */}
                  {quota != null && quota > 0 && (
                    <div className="mt-2">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] text-slate-400">Storage</span>
                        <span className="text-[10px] text-slate-500">{formatBytes(consumed)} / {formatBytes(quota)}</span>
                      </div>
                      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className={`h-1.5 rounded-full transition-all ${pct >= 90 ? 'bg-rose-500' : pct >= 70 ? 'bg-amber-400' : 'bg-violet-500'}`}
                          style={{ width: `${Math.min(pct, 100)}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add/Edit modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => { setShowModal(false); setEditingUser(null); setFormData(initialFormState); setModalMode('add'); }} />
          <div className="relative bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-base font-bold text-slate-900 mb-4">{modalMode === 'edit' ? 'Edit User' : 'Register New User'}</h3>
            <form onSubmit={handleSubmit} className="space-y-3">
              {[
                { label: 'Name', key: 'name', type: 'text', placeholder: 'Full name' },
                { label: 'Email', key: 'email', type: 'email', placeholder: 'email@example.com' },
                { label: 'Phone', key: 'phone', type: 'tel', placeholder: '+91 9999999999' },
              ].map(({ label, key, type, placeholder }) => (
                <div key={key}>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">{label}</label>
                  <input type={type} value={formData[key]} onChange={(e) => setFormData({ ...formData, [key]: e.target.value })}
                    className={inputCls} placeholder={placeholder} required />
                </div>
              ))}
              {modalMode !== 'edit' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Disk space (MB) <span className="text-slate-400 font-normal">— leave blank for unlimited</span></label>
                  <input type="text" inputMode="numeric" value={formData.disk_quota_mb}
                    onChange={(e) => setFormData({ ...formData, disk_quota_mb: e.target.value.replace(/\D/g, '') })}
                    className={inputCls} placeholder="e.g. 1024" />
                </div>
              )}
              <div className="flex gap-2 pt-2">
                <button type="button" onClick={() => { setShowModal(false); setEditingUser(null); setFormData(initialFormState); setModalMode('add'); }}
                  className="flex-1 px-4 py-2 border border-slate-200 rounded-xl text-slate-700 hover:bg-slate-50 text-sm font-medium">
                  Cancel
                </button>
                <button type="submit"
                  className="flex-1 px-4 py-2 rounded-xl text-sm font-semibold text-white transition-all"
                  style={{ background: 'linear-gradient(135deg, #7c3aed, #4f46e5)' }}>
                  {modalMode === 'edit' ? 'Save changes' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete confirm modal */}
      {deleteConfirmUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setDeleteConfirmUser(null)} />
          <div className="relative bg-white rounded-2xl shadow-xl max-w-sm w-full p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-rose-100 flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-rose-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                </svg>
              </div>
              <div>
                <h3 className="text-base font-bold text-slate-900">Delete user?</h3>
                <p className="text-sm text-slate-500 mt-1">
                  <strong>{deleteConfirmUser.name}</strong> and all their events, batches ({deleteConfirmUser.batch_count || 0}) and photos ({deleteConfirmUser.photo_count || 0}) will be permanently deleted.
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={() => setDeleteConfirmUser(null)} disabled={deleting}
                className="flex-1 px-4 py-2 border border-slate-200 rounded-xl text-slate-700 hover:bg-slate-50 text-sm font-medium">
                Cancel
              </button>
              <button type="button" onClick={handleDeleteUser} disabled={deleting}
                className="flex-1 px-4 py-2 bg-rose-600 text-white rounded-xl hover:bg-rose-700 disabled:opacity-50 text-sm font-semibold">
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
